/* eslint-disable @typescript-eslint/ban-types */
import { Field as FinalField } from 'react-final-form'
import { renderValidation } from '@flatsy/utils/Functions/form/Validation'
import { FieldsType } from '../../../Types'
import { Fields } from './Constant'
import TextField from './Text'
import SelectField from './Select'
import ColorPicker from './ColorPicker'
import PasswordField from './Password'
import AddressField from './Address'
import RadioField from './Radio'
import RadioButtonField from './RadioButton'
import DateField from './Date'
import ToggleField from './Toggle'
import CustomRatioField from './Custom/RatioField'
import CalendarInstruction from './Custom/CalendarInstruction'
import QualificationGuarantorType from './Custom/Qualification/GuarantoType'
import QualificationProfessionalSituation from './Custom/Qualification/ProfessionalSituation'
import MultipleSelectField from './MultipleSelect'
import QualificationCouple from './Custom/Qualification/Couple'

/* ----------------------------- RENDER FUNCTION ---------------------------- */
/**
 * @description scope function to render a single field
 * @param field
 * @param values
 */
const renderSingleField = <T extends {}>(
  field: FieldsType,
  values: T,
  justSubmitted: boolean
): React.ReactNode => {
  /**
   * @description check if field type is equal to text || number || tel || textarea
   */
  const shouldRenderTextField = () => {
    if (
      field.type === 'text' ||
      field.type === 'number' ||
      field.type === 'tel' ||
      field.type === 'textarea'
    ) {
      return true
    }
    return false
  }

  const fieldTypeName = (name: string) => {
    if (field.type === name) {
      return true
    }
    return false
  }

  /**
   * @description check if field type is equal to select and
   * have selectValue and selectInitialValue
   */
  const shouldRenderSelectField = () => {
    if (field.type === 'select' && field.selectValue && field.selectInitialValue) {
      return true
    }
    return false
  }

  /**
   * @description check if field type is equal to multipleSelect and
   * have selectValue and selectInitialValue
   */
  const shouldRenderMultipleSelectField = () => {
    if (field.type === 'multipleSelect' && field.selectValue && field.selectInitialValue) {
      return true
    }
    return false
  }

  /**
   * @description check if field type is equal to address
   */
  const shouldRenderAddressField = () => {
    return fieldTypeName('address')
  }

  /**
   * @description check if field type is equal to colorPicker
   */
  const shouldRenderColorPicker = () => {
    return fieldTypeName('colorPicker')
  }

  /**
   * @description check if field type is equal to password
   */
  const shouldRenderPasswordField = () => {
    return fieldTypeName('password')
  }

  /**
   * @description check if field type is equal to custom and field.custom exist
   */
  const shouldRenderCustomField = () => {
    if (field.type === 'custom' && field.custom) {
      return true
    }
    return false
  }

  /**
   * @description check if field type is equal to separator and field.custom exist
   */
  const shouldRenderSeparator = () => {
    if (field.type === 'separator' && field.components) {
      return true
    }
    return false
  }

  /**
   * @description check if field type is radio to colorPicker
   */
  const shouldRenderRadio = () => {
    if (field.type === 'radio' && field.selectValue && field.selectInitialValue) {
      return true
    }
    return false
  }

  /**
   * @description check if field type is equal to radioButton
   */
  const shouldRenderRadioButton = () => {
    if (field.type === 'radioButton' && field.selectValue) {
      return true
    }
    return false
  }

  const shoudlRenderDate = () => {
    return fieldTypeName('date')
  }

  const shouldRenderToggle = () => {
    return fieldTypeName('toggle')
  }

  const shouldRenderCustomCalendarInstruction = () => {
    return fieldTypeName('custom_calendar_instructions')
  }

  const shouldRenderCustomRatio = () => {
    return fieldTypeName('custom_ratio')
  }

  const shouldRenderCustomQualificationProfessionalSituation = () => {
    return fieldTypeName('custom_qualificationProfessionalSituation')
  }

  const shouldRenderCustomQualificationCouple = () => {
    return fieldTypeName('custom_qualificationCouple')
  }

  const shouldRenderCustomQualificationGuarantorType = () => {
    return fieldTypeName('custom_qualificationGuarantorType')
  }

  /**
   * @description return a field type as a string or false
   */
  const wichField = (): string | false => {
    const textField = shouldRenderTextField()
    const selectField = shouldRenderSelectField()
    const multipleSelectField = shouldRenderMultipleSelectField()
    const addressField = shouldRenderAddressField()
    const colorPicker = shouldRenderColorPicker()
    const passwordField = shouldRenderPasswordField()
    const custom = shouldRenderCustomField()
    const separator = shouldRenderSeparator()
    const radio = shouldRenderRadio()
    const radioButton = shouldRenderRadioButton()
    const date = shoudlRenderDate()
    const toggle = shouldRenderToggle()
    const customCalendarInstruction = shouldRenderCustomCalendarInstruction()
    const customRatio = shouldRenderCustomRatio()
    const customQualificationProfessionalSituation =
      shouldRenderCustomQualificationProfessionalSituation()
    const customQualificationCouple = shouldRenderCustomQualificationCouple()
    const customQualificationGuarantorType = shouldRenderCustomQualificationGuarantorType()

    if (textField) {
      return Fields.TEXT_FIELD
    }
    if (selectField) {
      return Fields.SELECT_FIELD
    }
    if (multipleSelectField) {
      return Fields.MULTIPLE_SELECT_FIELD
    }
    if (addressField) {
      return Fields.ADDRESS_FIELD
    }
    if (colorPicker) {
      return Fields.COLOR_PICKER
    }
    if (passwordField) {
      return Fields.PASSWORD_FIELD
    }
    if (custom) {
      return Fields.CUSTOM
    }
    if (separator) {
      return Fields.SEPARATOR
    }
    if (radio) {
      return Fields.RADIO
    }
    if (radioButton) {
      return Fields.RADIO_BUTTON
    }
    if (date) {
      return Fields.DATE
    }
    if (toggle) {
      return Fields.TOGGLE
    }
    if (customCalendarInstruction) {
      return Fields.CUSTOM_CALENDAR_INSTRUCTIONS
    }
    if (customRatio) {
      return Fields.CUSTOM_RATIO
    }
    if (customQualificationProfessionalSituation) {
      return Fields.CUSTOM_QUALIFICATION_PROFESSIONAL_SITUATION
    }
    if (customQualificationCouple) {
      return Fields.CUSTOM_QUALIFICATION_COUPLE
    }
    if (customQualificationGuarantorType) {
      return Fields.CUSTOM_QUALIFICATION_GUARANTOR_TYPE
    }
    return false
  }

  if (wichField() === Fields.SEPARATOR) {
    return field.components
  }

  return (
    <FinalField
      name={field.name}
      key={field.name}
      validate={renderValidation(field)}
      initialValue={field.selectInitialValue || undefined}
      validateFields={field.validateFields || []}
    >
      {({ input, meta }) => {
        if (wichField() === Fields.TEXT_FIELD) {
          return (
            <TextField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.SELECT_FIELD) {
          return (
            <SelectField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.MULTIPLE_SELECT_FIELD) {
          return (
            <MultipleSelectField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.RADIO) {
          return (
            <RadioField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.RADIO_BUTTON) {
          return (
            <RadioButtonField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.DATE) {
          return (
            <DateField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.TOGGLE) {
          return (
            <ToggleField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.ADDRESS_FIELD) {
          return (
            <AddressField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.COLOR_PICKER) {
          return (
            <ColorPicker
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.PASSWORD_FIELD) {
          return (
            <PasswordField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        if (wichField() === Fields.CUSTOM) {
          return (
            field.custom &&
            field.custom(
              input,
              field,
              meta.touched,
              meta.error,
              meta.submitError,
              meta.submitSucceeded,
              justSubmitted,
              meta.modified,
              values
            )
          )
        }

        if (wichField() === Fields.CUSTOM_CALENDAR_INSTRUCTIONS) {
          return (
            <CalendarInstruction
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }

        if (wichField() === Fields.CUSTOM_RATIO) {
          return (
            <CustomRatioField
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }

        if (wichField() === Fields.CUSTOM_QUALIFICATION_PROFESSIONAL_SITUATION) {
          return (
            <QualificationProfessionalSituation
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }

        if (wichField() === Fields.CUSTOM_QUALIFICATION_COUPLE) {
          return (
            <QualificationCouple
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }

        if (wichField() === Fields.CUSTOM_QUALIFICATION_GUARANTOR_TYPE) {
          return (
            <QualificationGuarantorType
              field={field}
              input={input}
              meta={meta}
              values={values}
              justSubmitted={justSubmitted}
            />
          )
        }
        return <div />
      }}
    </FinalField>
  )
}

export default renderSingleField
